<template>
  <OnBoarding>
    <template>
      <PTransparentLoader :loading="loading">
        <CRow>
          <CCol>
            <OnBoardingHeader :title="pageTitle"/>

            <CAlert show v-if="invalidCode || invitationError" color="danger">
              <translate translate-context="accept.error" v-if="invitationAlreadyAccepted">Invitation already accepted</translate>
              <translate translate-context="accept.error" v-else-if="invalidCode">Invalid invitation code</translate>
              <div class="mt-2 small">
                <translate translate-context="accept.error" v-if="invitationAlreadyAccepted">This invitation has already been accepted and cannot be used again.</translate>
                <translate translate-context="accept.error" v-else>This invitation may have been revoked or the code may be incorrect.</translate>
              </div>
            </CAlert>

            <CRow class="justify-content-center align-items-center text-center" v-if="!invalidCode && !invitationError && invitation">
              <CCol col="12">
                <div class="font-weight-medium"><translate translate-context="accept.invitation">You have been invited by:</translate></div>
                <div class="mb-2">{{ invitation.invited_by ? invitation.invited_by.name || invitation.invited_by.email : '' }}</div>

                <template v-if="invitation.pica_service">
                  <div class="font-weight-medium"><translate translate-context="accept.invitation">Event:</translate></div>
                  <div class="mb-2">{{ invitation.pica_service.name }}</div>
                </template>

                <template v-if="invitation.contract_manager">
                  <div class="font-weight-medium"><translate translate-context="accept.invitation">Contract Manager:</translate></div>
                  <div class="mb-2">{{ invitation.contract_manager.name }}</div>
                </template>
                
                <template v-if="invitation.scope_type === CollaboratorScope.GLOBAL">
                  <div class="font-weight-medium"><translate translate-context="accept.invitation">Global Access</translate></div>
                  <div class="mb-2"><translate translate-context="accept.invitation">You will have access to the entire system</translate></div>
                </template>
                
                <template v-if="invitation.roles && invitation.roles.length">
                  <div class="font-weight-medium"><translate translate-context="accept.invitation">Roles:</translate></div>
                  <div class="mb-3" v-for="role in invitation.roles" :key="role.id">
                    {{ role.name }}
                    <span v-if="role.expires_at" class="text-muted">
                      (<translate translate-context="accept.invitation">expires</translate> {{ new Date(role.expires_at).toLocaleDateString() }})
                    </span>
                  </div>
                </template>
              </CCol>
              <CCol col="12">
                <!-- Utente autenticato -->
                <template v-if="isAuthenticated">
                  <div class="font-weight-semi-bold mb-4 text-info">
                    <translate translate-context="accept.logged">You are already logged with account:</translate> 
                    {{ user.email }}, {{user.first_name}} {{user.last_name}}
                  </div>
                  <CButton @click="onAccept" block color="primary" class="mb-2" v-translate translate-context="accept.button">Accept</CButton>
                  <CButton @click="logout" color="secondary" block v-translate translate-context="accept.button">Change account</CButton>
                </template>
                
                <!-- Utente non autenticato -->
                <template v-else>
                  <CButton @click="onAccept" color="primary" block :disabled="acceptLoading">
                    <translate translate-context="accept.button">Accept invitation and login</translate>
                  </CButton>

                  <div class="text-center info">
                    <span v-translate translate-context="accept.info">otherwise</span>
                  </div>

                  <SocialLogin can-register/>
                  <AlreadyHaveAccount/>
                  
                  <CAlert v-if="error" color="danger" :show="!!error" class="mt-3">
                    {{ error }}
                  </CAlert>
                </template>
              </CCol>
            </CRow>
          </CCol>
        </CRow>
      </PTransparentLoader>
    </template>
  </OnBoarding>
</template>

<script>
import PTransparentLoader from "@/domain/core/components/PTransparentLoader.vue";
import SocialLogin from "@/domain/login/components/providers/SocialLoginButtons.vue";
import AlreadyHaveAccount from "@/domain/login/components/AlreadyHaveAccount.vue";
import {computed, methods} from "@/domain/login/mixin";
import OnBoarding from "@/domain/login/OnBoarding.vue";
import OnBoardingHeader from "@/domain/login/components/OnBoardingHeader.vue";
import {mapActions, mapGetters} from "vuex";
import CollaboratorScope from "@/domain/core/constant/collaboratorScope";

export default {
  name: 'Accept',
  components: {OnBoardingHeader, OnBoarding, PTransparentLoader, SocialLogin, AlreadyHaveAccount},
  async mounted() {
    try {
      await this.checkQueryParams()
    } catch (e) {
      // Cattura il tipo di errore per mostrare il messaggio appropriato
      if (e.message?.code) {
        this.invitationError = e.message.code
      }
    } finally {
      this.loading = false
    }

    // The accept page is only for user with an active invitation or that at least passed some parameter in the url
    if (!this.invitation && !this.$route.query.code)
      await this.$router.push({name: 'login'})
  },
  data() {
    return {
      loading: false,
      acceptLoading: false,
      error: null,
      invitationError: null,
      CollaboratorScope
    }
  },
  computed: {
    ...computed,
    ...mapGetters('login', ['user']),
    invalidCode() {
      return !this.invitation && this.$route.query.code && !this.loading
    },
    invitationAlreadyAccepted() {
      return this.invitationError === 'invalid_collaborator_status'
    },
    pageTitle() {
      if (this.invitationAlreadyAccepted) {
        return this.$pgettext('accept.title', 'Invitation already used')
      }
      if (this.invalidCode || this.invitationError) {
        return this.$pgettext('accept.title', 'Invalid invitation')
      }
      return this.$pgettext('accept.title', 'Accept invitation')
    }
  },
  methods: {
    ...methods,
    ...mapActions('login', ['logout', 'getCurrentUser', 'redirectAfterAccept']),
    async onAccept() {
      this.acceptLoading = true
      this.error = null
      
      try {
        const code = this.$route.query.code
        const email = this.$route.query.email
        
        // Accetta l'invito
        const require_login = await this.acceptInvitation({ code, email })
        
        // Se l'utente non è autenticato, il backend ha inviato un token via email
        if (require_login) {
          // Redirect al login - il pendingTokenLogin nello store farà apparire automaticamente il token input
          // L'invitation rimane nello store per il redirect post-login
          await this.$router.push({
            name: 'login'
          })
          return
        }
        
        // Utente già autenticato, usa la funzione centralizzata per il redirect
        // che verifica i permessi e pulisce l'invitation
        await this.redirectAfterAccept(this.$router)
      } catch (error) {
        // Gestione errori secondo la documentazione API
        if (error.message?.code === 'invalid_invitation_code') {
          this.error = this.$pgettext('accept.error', 'Invalid invitation code')
        } else if (error.message?.code === 'invalid_collaborator_status') {
          this.error = this.$pgettext('accept.error', 'This invitation has already been accepted')
        } else if (error.message?.email) {
          this.error = error.message.email[0]
        } else if (error.message?.code) {
          this.error = error.message.code[0]
        } else {
          this.error = error.message?.detail || this.$pgettext('accept.error', 'An error occurred')
        }
      } finally {
        this.acceptLoading = false
      }
    }
  }
}
</script>
